import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Users,
  ShieldCheck,
  Building2,
  ClipboardList,
  Compass,
  MessagesSquare,
  Sparkles,
  MapPin,
  Calendar,
  BarChart3,
  FileSignature,
  Palmtree,
  HardHat,
  UserCheck,
  Landmark,
  Brain,
} from "lucide-react";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";

const FOCUS_STATES = [
  { code: "TX", label: "Texas", note: "DFW · Houston · Austin · San Antonio" },
  { code: "CA", label: "California", note: "LA · Bay Area · Coastal Dev" },
  { code: "FL", label: "Florida", note: "Miami-Dade · Hillsborough · Orange" },
  { code: "AZ", label: "Arizona", note: "Phoenix · Maricopa · Pima" },
  { code: "NJ", label: "New Jersey", note: "Bergen · Hudson · Morris" },
  { code: "NC", label: "North Carolina", note: "Mecklenburg · Wake · Durham" },
  { code: "IL", label: "Illinois", note: "Chicago · DuPage · Cook" },
  { code: "+43", label: "All 50 States", note: "Coast to coast coverage" },
];

const LIFECYCLE = [
  { phase: "01", title: "Intake & Scoping", body: "We translate your scope into a code-aware permit strategy. AHJ identification, fee estimation, and risk flags inside 48 hours." },
  { phase: "02", title: "Due Diligence", body: "Site Investigation Reports, zoning verification, code feasibility — before you sign the lease or buy the dirt. We've saved clients six-figure mistakes here." },
  { phase: "03", title: "Zoning & Entitlements", body: "Variance, CUP, special-use, Coastal Dev — we prep the narrative, walk it through DRC, and present where required." },
  { phase: "04", title: "Plan Review & Submittal", body: "Complete, correct, compliant submittals on the first try. We know what each AHJ wants and how to package it — Trakit, EnerGov, ePlans, you name it." },
  { phase: "05", title: "AHJ Communications", body: "We talk to the building official, the planner, the fire marshal — so your architect doesn't have to. Pre-meetings scheduled, comments resolved, expectations set." },
  { phase: "06", title: "Inspections → C/O", body: "Every inspection coordinated, every final sign-off chased. We don't celebrate until the Certificate of Occupancy is in your hand." },
];

const WHO_WE_ARE = [
  {
    icon: Users,
    title: "Customer-Centric",
    body: "We build long-term relationships, learn each customer's unique goals, and identify exactly how we can support their growth. Virtual or in-person — we show up where you need us.",
  },
  {
    icon: ShieldCheck,
    title: "Construction Permitting Experts",
    body: "Hands-on experience across the full permit lifecycle, with deep knowledge of IBC / IRC / IFC, ADA, zoning ordinances, and multi-jurisdictional compliance.",
  },
  {
    icon: BarChart3,
    title: "Data-Driven, Experience-Led",
    body: "We gather and interpret jurisdictional data to sharpen every customer conversation — but it's the pattern recognition from real submittals that actually moves projects forward.",
  },
  {
    icon: Sparkles,
    title: "Tech-Forward & Adaptable",
    body: "From plan review platforms (Trakit, EnerGov, ePlans) to AI-assisted research and live workspace dashboards — we use technology to compress timelines, not replace expertise.",
  },
  {
    icon: Building2,
    title: "Team Development",
    body: "We invest in cross-functional knowledge so any teammate on your project can step in fluently across intake, review, compliance, and AHJ engagement.",
  },
];

const ADDITIONAL_SERVICES = [
  { icon: ShieldCheck, title: "Business Licensing", body: "State and municipal business license filings — new entity, change of ownership, location change. We handle the forms and chase the approvals." },
  { icon: CheckCircle2, title: "Certificate of Occupancy", body: "Final C/O coordination — inspection scheduling, punch-list resolution, AHJ sign-off chase, and the certificate in your hand." },
  { icon: FileSignature, title: "Zoning Certificate / Verification", body: "Zoning verification letters and use confirmations — for leases, sales, financing, or change-of-use due diligence." },
  { icon: Compass, title: "Civil Coordination", body: "Civil engineering coordination across site plan, grading, storm/sanitary tap, and utility approvals — pulling the disciplines together so nothing gets lost between consultants." },
  { icon: Users, title: "AOR Coordination", body: "Architect of Record coordination across multi-state portfolios — local AOR engagement, seal management, and design review consistency." },
  { icon: Palmtree, title: "Hawaii Permit Coordination", body: "Hawaii is its own permitting universe — Honolulu DPP, Maui, Hawaii County, Kauai. We've worked these jurisdictions and know the cadence." },
  { icon: MessagesSquare, title: "Pre-Meeting with Planning Department", body: "Scheduled pre-application meetings with Planning — set expectations, surface variance risk, and lock in a clear submittal path before the clock starts." },
  { icon: HardHat, title: "Pre-Construction AHJ Inspection Coordination", body: "Pre-con AHJ walks and inspection scheduling — building, fire, MEP, and health — synced with your GC's mobilization schedule." },
];

const RESPONSIBILITIES = [
  { icon: ClipboardList, title: "Manage the Permit Process", body: "End-to-end lifecycle ownership — research requirements, prepare forms, gather documentation, submit to authorities." },
  { icon: BarChart3, title: "Generate Reports", body: "Detailed progress reports with clear, regular updates to you and your stakeholders. No mystery." },
  { icon: Compass, title: "Optimize Processes", body: "Continuous improvement — best practices that streamline workflows, reduce bottlenecks, and protect schedule." },
  { icon: ShieldCheck, title: "Ensure Compliance & Accuracy", body: "Every application complete, accurate, and aligned with jurisdictional standards before it leaves our desk." },
  { icon: MessagesSquare, title: "Monitor & Expedite Approvals", body: "Proactive status tracking and direct AHJ engagement to keep applications moving forward." },
];

const PROOF_POINTS = [
  { stat: "3,221", label: "U.S. counties indexed" },
  { stat: "All 50", label: "States covered" },
  { stat: "500+", label: "Commercial projects delivered" },
  { stat: "1000+", label: "Due Diligence / Site Investigation Reports" },
];

export default function Services() {
  return (
    <div className="min-h-screen bg-white" data-testid="services-page">
      <Header />

      {/* HERO */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-emerald-50/40 via-white to-white">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 mb-6" data-testid="services-tag">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-emerald-700">Services · Full Lifecycle Expertise</span>
            </div>
            <h1 className="font-outfit text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-slate-900 leading-[1.02]">
              Lean on us to <br />
              <span className="text-emerald-700">get it done.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed">
              Real-person permit coordination with weekly check-ins. Exceptional customer service.
              On-time deliverables. If you're stuck in a bind from intake, zoning, planning, all
              the way through Certificate of Occupancy — we know the path through.
            </p>
            <div className="mt-10 flex flex-wrap gap-3" data-testid="services-cta-row">
              <Button asChild size="lg" className="bg-slate-900 hover:bg-slate-800 text-white h-12 px-7" data-testid="services-hire-cta">
                <Link to="/hire">Hire us as your expediter<ArrowRight className="ml-2 w-4 h-4" /></Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="h-12 px-7 border-slate-300" data-testid="services-verify-cta">
                <Link to="/ahj-verify">Verify your AHJ<ArrowRight className="ml-2 w-4 h-4" /></Link>
              </Button>
            </div>
            <p className="mt-6 text-xs font-mono uppercase tracking-[0.18em] text-slate-500" data-testid="services-tagline">
              &ldquo;Experience from both sides of the counter.&rdquo;
            </p>
          </div>

          {/* Proof column */}
          <div className="lg:col-span-5 grid grid-cols-2 gap-4 self-center">
            {PROOF_POINTS.map((p) => (
              <div key={p.label} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid={`services-proof-${p.stat}`}>
                <div className="font-outfit text-4xl font-bold text-slate-900">{p.stat}</div>
                <div className="mt-2 text-xs text-slate-600 leading-snug">{p.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI + HUMAN EXPERTISE BAND (Services) */}
      <section className="bg-slate-900 text-white border-b border-slate-800" data-testid="services-expert-band">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-14 md:py-20">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 mb-5">
              <UserCheck className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-emerald-400">AI Speed · Expert Reviewed · Human Insight</span>
            </div>
            <h2 className="font-outfit text-4xl md:text-5xl font-bold tracking-tight leading-[1.05]">
              We&apos;ve sat on the other side of the counter.
            </h2>
            <p className="mt-6 text-base md:text-lg text-slate-300 leading-relaxed max-w-3xl mx-auto">
              Coast To Coast Permitting is led by a real <strong className="text-white">Plans Examiner</strong> —
              backed by experienced professionals who have worked at AHJs as consultants, code officials,
              and plan reviewers. We know the realistic side of permitting that only a real person who&apos;s
              <strong className="text-white"> taken projects vertical </strong> can understand. AI gives
              us speed and pattern recognition. Human experience gives us the call that actually moves
              the project.
            </p>
            <p className="mt-5 text-sm md:text-base font-mono uppercase tracking-[0.16em] text-emerald-400">
              We know what the AHJ is thinking — before they say it.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto" data-testid="services-expert-pillars">
            {[
              { icon: UserCheck, title: "Plans Examiner Led", body: "Real plan review experience — not just AI output. A human reviews every project decision." },
              { icon: Landmark, title: "AHJ Insider", body: "Worked inside Building, Planning, Fire, Public Works. We know how reviewers actually think." },
              { icon: Building2, title: "Vertical Build Field", body: "We've taken projects from intake through C/O. We know what the construction reality requires." },
              { icon: Brain, title: "AI + Human Judgment", body: "Claude Haiku 4.5 for speed and breadth. Plans Examiner judgment on the call that matters." },
            ].map((p) => {
              const Ico = p.icon;
              return (
                <div key={p.title} className="bg-slate-800/60 border border-slate-700 rounded-xl p-5 hover:border-emerald-500/40 transition-colors">
                  <div className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 flex items-center justify-center mb-3">
                    <Ico className="w-4 h-4" />
                  </div>
                  <div className="font-outfit font-bold text-white text-base leading-tight mb-1.5">{p.title}</div>
                  <div className="text-xs text-slate-300 leading-relaxed">{p.body}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FOCUS COVERAGE */}
      <section className="border-b border-slate-200 bg-white">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="max-w-3xl">
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-emerald-700 mb-3">Coverage</div>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
              Multi-state focus, nationwide delivery.
            </h2>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              We've physically worked permits in every region of the country. These are the markets
              where our pattern recognition compounds the fastest — but if your project sits outside
              this list, we still bring the same playbook.
            </p>
          </div>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="services-states-grid">
            {FOCUS_STATES.map((s) => (
              <div key={s.code} className="bg-slate-50 border border-slate-200 rounded-xl p-4 hover:border-emerald-300 hover:bg-emerald-50/40 transition-colors" data-testid={`focus-state-${s.code}`}>
                <div className="flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5 text-emerald-600" />
                  <span className="font-outfit font-bold text-slate-900 text-base">{s.label}</span>
                </div>
                <div className="mt-1.5 text-[11px] font-mono uppercase tracking-[0.14em] text-slate-500">{s.note}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LIFECYCLE PHASES */}
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-emerald-700 mb-3">The Lifecycle</div>
              <h2 className="font-outfit text-3xl md:text-4xl font-bold text-slate-900 tracking-tight leading-tight">
                From intake through C/O — handled.
              </h2>
              <p className="mt-5 text-base text-slate-600 leading-relaxed">
                Most expediters do submittal and inspection. We do the full lifecycle —
                including the messy upstream work (due diligence, zoning narrative, pre-meetings)
                that decides whether a project ever gets built at all.
              </p>
              <div className="mt-6 inline-flex items-center gap-2 px-3 py-2 rounded-full bg-emerald-50 border border-emerald-200">
                <Calendar className="w-3.5 h-3.5 text-emerald-700" />
                <span className="text-xs font-mono uppercase tracking-[0.16em] text-emerald-700">Weekly status check-ins · always</span>
              </div>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="services-lifecycle-grid">
              {LIFECYCLE.map((l) => (
                <div key={l.phase} className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg hover:border-emerald-200 transition-all" data-testid={`lifecycle-${l.phase}`}>
                  <div className="font-mono text-xs text-emerald-700 mb-2">{l.phase}</div>
                  <h3 className="font-outfit text-xl font-bold text-slate-900 mb-2">{l.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{l.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ADDITIONAL SERVICES — discrete service menu */}
      <section className="border-b border-slate-200 bg-white" data-testid="additional-services-section">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="max-w-3xl">
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-emerald-700 mb-3">Service Menu</div>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
              Additional services we own.
            </h2>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              Beyond the full-lifecycle work, these are the discrete engagements we&apos;re asked
              for most often — bookable as standalone services or bundled into a larger program.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="additional-services-grid">
            {ADDITIONAL_SERVICES.map((s) => {
              const Ico = s.icon;
              const slug = s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
              return (
                <div key={s.title} className="bg-slate-50 border border-slate-200 rounded-2xl p-5 hover:bg-white hover:border-emerald-300 hover:shadow-md transition-all" data-testid={`addl-${slug}`}>
                  <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center mb-3">
                    <Ico className="w-4 h-4" />
                  </div>
                  <h3 className="font-outfit text-base font-bold text-slate-900 mb-1.5 leading-snug">{s.title}</h3>
                  <p className="text-xs text-slate-600 leading-relaxed">{s.body}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="additional-services-cta-row">
            <Button asChild size="lg" className="bg-slate-900 hover:bg-slate-800 text-white h-12 px-7" data-testid="addl-services-hire-cta">
              <Link to="/hire">Request any of these services<ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="h-12 px-7 border-slate-300" data-testid="addl-services-pricing-cta">
              <Link to="/pricing">See pricing & DD reports<ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* WHO WE ARE */}
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="max-w-3xl">
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-emerald-700 mb-3">Who We Are</div>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
              Five principles that drive every project.
            </h2>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              The people, the playbook, the standards. Here's what shows up every day on your
              project — whether it's a 2,500-square-foot tenant improvement or a national rollout
              across 12 jurisdictions.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="who-we-are-grid">
            {WHO_WE_ARE.map((w) => {
              const Ico = w.icon;
              return (
                <div key={w.title} className="bg-slate-50 border border-slate-200 rounded-2xl p-6 hover:bg-white hover:shadow-md transition-all" data-testid={`who-${w.title.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-700 mb-4">
                    <Ico className="w-5 h-5" />
                  </div>
                  <h3 className="font-outfit text-lg font-bold text-slate-900 mb-2">{w.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{w.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHAT WE OWN */}
      <section className="border-b border-slate-200 bg-slate-900 text-white">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-emerald-400 mb-3">What We Own on Your Project</div>
              <h2 className="font-outfit text-3xl md:text-4xl font-bold tracking-tight leading-tight">
                Five jobs we never hand off.
              </h2>
              <p className="mt-5 text-base text-slate-300 leading-relaxed">
                You hire us, you get a real human who owns each of these end-to-end. No offshore
                handoffs, no layers, no &ldquo;we&apos;ll get back to you next week.&rdquo;
              </p>
              <div className="mt-8">
                <Button asChild size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 h-12 px-7 font-semibold" data-testid="services-bottom-cta">
                  <Link to="/hire">Start a project<ArrowRight className="ml-2 w-4 h-4" /></Link>
                </Button>
              </div>
            </div>
            <div className="lg:col-span-7 space-y-3" data-testid="services-responsibilities">
              {RESPONSIBILITIES.map((r, i) => {
                const Ico = r.icon;
                return (
                  <div key={r.title} className="flex gap-4 p-5 bg-slate-800/60 border border-slate-700 rounded-xl hover:border-emerald-500/40 transition-colors">
                    <div className="flex-none w-10 h-10 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                      <Ico className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="font-mono text-[11px] text-emerald-400 mb-0.5">0{i + 1}</div>
                      <h3 className="font-outfit text-lg font-bold text-white mb-1">{r.title}</h3>
                      <p className="text-sm text-slate-300 leading-relaxed">{r.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CLOSING */}
      <section className="bg-white">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-emerald-700 mb-3">Ready when you are</div>
            <h2 className="font-outfit text-3xl md:text-5xl font-bold text-slate-900 tracking-tight leading-tight">
              Stop chasing endless AHJ portals.
            </h2>
            <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
              Tell us the scope, the jurisdiction, the timeline. We&apos;ll come back inside 24 hours
              with a real plan, a real quote, and a real person assigned to your project.
            </p>
            <div className="mt-10 flex flex-wrap gap-3 justify-center">
              <Button asChild size="lg" className="bg-slate-900 hover:bg-slate-800 text-white h-12 px-7" data-testid="services-final-hire">
                <Link to="/hire">Hire us as your expediter<ArrowRight className="ml-2 w-4 h-4" /></Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="h-12 px-7 border-slate-300" data-testid="services-final-pricing">
                <Link to="/pricing">See subscription pricing<ArrowRight className="ml-2 w-4 h-4" /></Link>
              </Button>
            </div>
            <div className="mt-12 flex items-center justify-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span className="text-sm text-slate-700">24-hour quote response · Real human owner · Weekly check-ins</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
